import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  Plus, 
  Users, 
  Settings,
  ChevronRight,
  ScanLine,
  FileEdit,
  Menu,
  X,
  BarChart3
} from 'lucide-react';
import Calculator from './Calculator';
import GlobalSearch from './GlobalSearch';
import { getTranslation } from '../i18n/translations';

const Sidebar = ({ user, lang = 'tr' }) => {
  const t = (key) => getTranslation(lang, key);
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    }
  }, [location.pathname, isMobile]);

  const menuItems = [
    { 
      id: 'home', 
      label: t('navHome'), 
      icon: Home, 
      path: '/dashboard' 
    },
    { 
      id: 'proposals', 
      label: t('navProposals'), 
      icon: FileText, 
      path: '/proposals' 
    },
    { 
      id: 'new-proposal', 
      label: t('newProposal'), 
      icon: Plus, 
      path: '/proposal/new' 
    },
    { 
      id: 'text-documents', 
      label: t('navTextDocs'), 
      icon: FileEdit, 
      path: '/text-documents',
      subItems: [
        {
          id: 'scan-image',
          label: t('scanImage'),
          icon: ScanLine,
          path: '/ocr-editor'
        }
      ]
    },
    { 
      id: 'customers', 
      label: t('navCustomers'), 
      icon: Users, 
      path: '/customers' 
    },
    { 
      id: 'reports', 
      label: t('navReports'), 
      icon: BarChart3, 
      path: '/reports' 
    },
    { 
      id: 'settings', 
      label: t('settings'), 
      icon: Settings, 
      path: '/settings' 
    },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === '/proposals') {
      return location.pathname === '/proposals';
    }
    if (path === '/proposal/new') {
      return location.pathname === '/proposal/new';
    }
    if (path === '/text-documents') {
      return location.pathname === '/text-documents' || location.pathname.startsWith('/ocr-editor');
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Header with Hamburger */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-gradient-to-r from-[#1a365d] to-slate-900 text-white z-50 flex items-center justify-between px-4 shadow-lg">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition"
            data-testid="hamburger-menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <img 
            src="/nkt-bee-logo.png" 
            alt="NKT Logo" 
            className="w-8 h-8 object-contain bg-white rounded-lg p-0.5"
          />
          <span className="font-medium text-sm tracking-wide">NKT - Teklif Hazırla</span>
        </div>
        
        {/* Mobile user avatar + search icon */}
        <div className="flex items-center gap-1">
          <GlobalSearch mode="mobile" lang={lang} />
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-8 h-8 rounded-full border-2 border-amber-400"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-sm">
              {user?.name?.charAt(0) || 'U'}
            </div>
          )}
        </div>
      </header>

      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-screen bg-gradient-to-b from-[#1a365d] to-slate-900 text-white z-40 flex flex-col transition-transform duration-300 ease-in-out
        ${isMobile ? 'w-72' : 'w-64'}
        ${isMobile ? (isOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
        ${isMobile ? 'pt-14' : 'pt-0'}
      `}>
        {/* Logo - Desktop only */}
        <div className="hidden lg:flex p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img 
              src="/nkt-bee-logo.png" 
              alt="NKT Logo" 
              className="w-11 h-11 object-contain bg-white rounded-lg p-1 flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="font-normal text-base whitespace-nowrap">NKT - Teklif Hazırla</h1>
              <p className="text-[11px] text-slate-400 font-light">{t('appTagline')}</p>
            </div>
          </div>
        </div>

        {/* Global Search — Desktop */}
        <div className="hidden lg:block px-4 pt-4">
          <GlobalSearch mode="desktop" lang={lang} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const hasSubItems = item.subItems && item.subItems.length > 0;
              
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavigate(item.path)}
                    data-testid={`sidebar-${item.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
                      active
                        ? 'bg-white/15 text-white border-r-4 border-amber-400'
                        : 'text-slate-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon 
                      className={`w-5 h-5 flex-shrink-0 ${active ? 'text-amber-400' : 'text-slate-400 group-hover:text-white'}`} 
                      strokeWidth={1.5}
                    />
                    <span className="font-medium truncate">{item.label}</span>
                    {active && (
                      <ChevronRight className="w-4 h-4 ml-auto text-amber-400 flex-shrink-0" />
                    )}
                  </button>
                  
                  {/* Sub Items */}
                  {hasSubItems && active && (
                    <ul className="mt-1 ml-4 space-y-1">
                      {item.subItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const subActive = location.pathname === subItem.path || location.pathname.startsWith(subItem.path + '/');
                        return (
                          <li key={subItem.id}>
                            <button
                              onClick={() => handleNavigate(subItem.path)}
                              data-testid={`sidebar-${subItem.id}`}
                              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 text-sm ${
                                subActive
                                  ? 'bg-amber-500/20 text-amber-400'
                                  : 'text-slate-400 hover:bg-white/5 hover:text-white'
                              }`}
                            >
                              <SubIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                              <span className="truncate">{subItem.label}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-10 h-10 rounded-full border-2 border-amber-400 flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                {user?.name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name || t('userFallback')}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Calculator FAB - Fixed position, responsive */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <Calculator />
      </div>

      {/* Spacer for mobile header */}
      <div className="lg:hidden h-14" />
    </>
  );
};

export default Sidebar;
